import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "@/lib/content";

const MD_LINK = /\[\[(?:PRZYCISK|BUTTON)(?:[:\s]+([^\]|]*?))?(?:\|\s*([^\]]*?))?\s*\]\]|\[([^\]]+)\]\(([^)\s]+)\)|<a\s+[^>]*?href=["']?([^"'\s>]+)["']?[^>]*>([^<]*)<\/a>/gi;
const ORDER_PATH = "/biuro-wirtualne-warszawa/zamow-pakiet";
const DEFAULT_BUTTON = { pl: "Wybierz pakiet", en: "Choose a plan", de: "Paket wählen", fr: "Choisir un forfait", tr: "Paket seçin", uk: "Обрати пакет" };
const SAME_SITE = /^https?:\/\/(?:www\.)?twojebiuro\.waw\.pl(?=\/|$)/i;

function normalizeHref(href) {
  const h = href.trim();
  if (SAME_SITE.test(h)) return h.replace(SAME_SITE, "") || "/";
  if (typeof window !== "undefined" && h.startsWith(window.location.origin)) return h.slice(window.location.origin.length) || "/";
  if (!/^(https?:\/\/|mailto:|tel:|\/|#)/i.test(h)) return "#";
  return h;
}
const DOCS_RE = /(?:generator\p{L}*\s+dokument\p{L}+|wz[oó]r\p{L}*\s+dokument\p{L}+|document generator|document templates?)/iu;
const ORDER_RE = /(?:„?zam[oó]w pakiet”?|„?wybierz pakiet”?|formularz\p{L}*\s+zam[oó]wieni\p{L}+|„?choose a plan”?|„?order a package”?|order form)/iu;
const KEYWORDS = new RegExp(`(${DOCS_RE.source}|${ORDER_RE.source})`, "giu");

const linkCls = "underline underline-offset-2 decoration-brand-teal/60 font-semibold text-foreground hover:text-brand-teal transition-colors";
const btnCls = "not-prose inline-flex items-center gap-2 my-2 bg-brand-teal hover:bg-brand-tealDark text-white font-semibold px-6 py-3 rounded-full text-sm no-underline transition-colors";

export function openOrderDialog(navigate, onLanding) {
  if (onLanding) {
    window.dispatchEvent(new CustomEvent("tb:open-order", { detail: null }));
    return;
  }
  sessionStorage.setItem("tb:pending-order", "1");
  navigate("/#biuro-wirtualne-cennik");
}

function Smart({ href, children, testid, button = false }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onLanding = pathname === "/";
  const cls = button ? btnCls : linkCls;
  const mark = () => {
    const m = pathname.match(/\/wiedza\/([^/?#]+)/);
    if (m) sessionStorage.setItem("tb:order-source", `article:${m[1]}`);
  };
  if (href === "order") {
    return <button type="button" data-testid={testid} onClick={() => { mark(); openOrderDialog(navigate, onLanding); }} className={cls}>{children}</button>;
  }
  if (href.startsWith("#")) {
    const go = (e) => { e.preventDefault(); onLanding ? scrollToSection(href) : navigate(`/${href}`); };
    return <a data-testid={testid} href={onLanding ? href : `/${href}`} onClick={go} className={cls}>{children}</a>;
  }
  if (href.startsWith("/")) return <Link data-testid={testid} to={href} onClick={href.startsWith(ORDER_PATH) ? mark : undefined} className={cls}>{children}</Link>;
  return <a data-testid={testid} href={href} target="_blank" rel="noopener noreferrer" className={cls}>{children}</a>;
}

function autolink(text, keyBase) {
  return text.split(KEYWORDS).filter((p) => p !== undefined && p !== "").map((part, i) => {
    if (DOCS_RE.test(part) && part.match(DOCS_RE)[0] === part) return <Smart key={`${keyBase}-d${i}`} href="/biuro-wirtualne-warszawa/dokumenty" testid="richtext-docs-link">{part}</Smart>;
    if (ORDER_RE.test(part) && part.match(ORDER_RE)[0] === part) return <Smart key={`${keyBase}-o${i}`} href="order" testid="richtext-order-link">{part}</Smart>;
    return part;
  });
}

export function RichText({ text }) {
  if (!text) return null;
  const out = [];
  let last = 0;
  let i = 0;
  const lang = (typeof document !== "undefined" && document.documentElement.lang) || "pl";
  for (const m of String(text).matchAll(MD_LINK)) {
    if (m.index > last) out.push(...autolink(text.slice(last, m.index), `t${i}`));
    if (/^\[\[/.test(m[0])) {
      const label = (m[1] || "").trim() || DEFAULT_BUTTON[lang] || DEFAULT_BUTTON.pl;
      out.push(<Smart key={`b${i}`} href={normalizeHref((m[2] || "").trim() || ORDER_PATH)} testid="richtext-button" button>{label}</Smart>);
    } else {
      out.push(<Smart key={`l${i}`} href={normalizeHref(m[4] || m[5])} testid="richtext-link">{(m[3] || m[6] || "").trim()}</Smart>);
    }
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) out.push(...autolink(text.slice(last), `t${i}`));
  return <>{out}</>;
}
